/* ============================================================
   SignageWorks — 2027 Budget Planner
   ============================================================
   Four steps: property -> what you have -> how many -> review and send.

   PRICING RULE, from PR 3 (Budget Planner 2.0): THE BROWSER DOES NOT KNOW
   ANY PRICES. Not hidden ones, not ones behind a flag — there are no unit
   prices in this file and no arithmetic that could produce one. The catalog
   below carries a key, a name and a size, and nothing else. The server is
   the sole pricing authority, and the customer's budget arrives by email
   quoting BOTH materials side by side.

   That is why step 4 is Review & Send rather than a reveal: there is
   nothing to reveal here. The customer confirms what they told us, and the
   numbers come back in the email.

   Quantity rule: customers enter their own counts. There is no derived math
   anywhere in this file. Quantities are digits only, whole numbers, up to
   99,999 per line — a raw text field with inputmode="numeric" rather than
   <input type="number">, because a number input silently accepts '1e3' and
   hands back 1000. maxlength is not the answer either — it drops the extra
   character silently instead of refusing it. The server validates the same
   rules again and is the authority; this only saves a round trip.

   Projects over 1,000 signs are not automatically priced. The server says so
   in its response and this file shows the Under Review completion instead.
   ============================================================ */

(function () {
  'use strict';

  var root = document.querySelector('[data-sw-planner]');
  if (!root) return;

  var ENDPOINT = root.getAttribute('data-endpoint') || '/api/budget-request';

  /* No prices. Deliberately. See the note at the top of this file. */
  var CATALOG = [
    { g:'monument',  k:'mon96',   n:'Monument Face Replacement',         s:'48" x 96"' },
    { g:'monument',  k:'mon48',   n:'Monument Face Replacement',         s:'38" x 48"' },
    { g:'leasing',   k:'lowall',  n:'Leasing Office — For Wall',         s:'18" x 24"' },
    { g:'leasing',   k:'lopost',  n:'Leasing Office — For Post',         s:'24" x 36"' },
    { g:'leasing',   k:'lodir',   n:'Leasing Office Directional',        s:'18" x 24"' },
    { g:'leasing',   k:'maint',   n:'Maintenance Room',                  s:'18" x 24"' },
    { g:'buildings', k:'bldadr',  n:'Building Address',                  s:'18" x 24"' },
    { g:'buildings', k:'blddir',  n:'Building Directional',              s:'24" x 36"' },
    { g:'buildings', k:'unit',    n:'Unit Numbers',                      s:'4" x 9"' },
    { g:'parking',   k:'future',  n:'Future Resident Parking',           s:'18" x 24"' },
    { g:'parking',   k:'nopark',  n:'No Parking Anytime',                s:'12" x 18"' },
    { g:'pool',      k:'poolr',   n:'Pool Rules',                        s:'24" x 36"' },
    { g:'pool',      k:'nolife',  n:'No Lifeguard On Duty',              s:'48" x 36.5"' },
    { g:'pool',      k:'shallow', n:'Shallow Water / No Diving',         s:'48" x 36.5"' },
    { g:'fitness',   k:'fit',     n:'Fitness Center',                    s:'8" x 36"' },
    { g:'fitness',   k:'fitr',    n:'Fitness Center Rules',              s:'18" x 24"' },
    { g:'laundry',   k:'laun',    n:'Laundry Room',                      s:'8" x 36"' },
    { g:'mail',      k:'mail',    n:'Mail Center',                       s:'8" x 36"' },
    { g:'dogpark',   k:'dog',     n:'Dog Park Rules',                    s:'18" x 24"' },
    { g:'play',      k:'playr',   n:'Play Area Rules',                   s:'18" x 24"' },
    { g:'play',      k:'playd',   n:'Play Area Directional',             s:'18" x 24"' },
    { g:'grill',     k:'grill',   n:'Grilling & Picnic Area',            s:'18" x 24"' },
    { g:'firepit',   k:'fire',    n:'Fire Pit Rules',                    s:'24" x 36"' },
    { g:'carcare',   k:'car',     n:'Car Care Center',                   s:'24" x 36"' },
    { g:'dumpster',  k:'dump',    n:'Dumpster Rules',                    s:'24" x 36"' },
    { g:'pond',      k:'fish',    n:'No Fishing',                        s:'18" x 24"' },
    { g:'security',  k:'priv24',  n:'Private Property / No Trespassing', s:'18" x 24"' },
    { g:'security',  k:'priv18',  n:'Private Property / No Trespassing', s:'12" x 18"' }
  ];

  var GROUPS = [
    { g:'monument',  label:'Monument sign',     icon:'swi-pricehold' },
    { g:'leasing',   label:'Leasing office',    icon:'swi-visualize' },
    { g:'buildings', label:'Buildings & units', icon:'swi-visualize' },
    { g:'parking',   label:'Parking',           icon:'swi-order' },
    { g:'pool',      label:'Pool',              icon:'swi-quality' },
    { g:'fitness',   label:'Fitness center',    icon:'swi-fast' },
    { g:'laundry',   label:'Laundry',           icon:'swi-logos' },
    { g:'mail',      label:'Mail center',       icon:'swi-shipping' },
    { g:'dogpark',   label:'Dog park',          icon:'swi-check' },
    { g:'play',      label:'Playground',        icon:'swi-check' },
    { g:'grill',     label:'Grilling area',     icon:'swi-check' },
    { g:'firepit',   label:'Fire pit',          icon:'swi-check' },
    { g:'carcare',   label:'Car care center',   icon:'swi-order' },
    { g:'dumpster',  label:'Dumpsters',         icon:'swi-check' },
    { g:'pond',      label:'Pond / lake',       icon:'swi-check' },
    { g:'security',  label:'Property signage',  icon:'swi-noobligation' }
  ];

  var picked = {};
  /* Parsed state per line: a non-negative count, or -1 for "the field is
     holding something we would have to guess about". */
  var qty = {};
  /* The RAW characters the customer typed, kept verbatim.
     Step 3 is rebuilt from scratch every time it is entered, and rebuilding
     from `qty` alone would repaint an unusable field as "-1" — replacing what
     someone typed with a number they never wrote, which is precisely the silent
     transformation this release exists to remove. */
  var qtyRaw = {};
  /* '' until the customer chooses. Required, and enforced on Continue. */
  var projectType = '';
  var submitting = false;

  /* Approved 2026-09-04. This is a sanity ceiling on ONE field, not a pricing
     threshold: anything over 1,000 signs in total is routed to manual review by
     the server regardless. It is deliberately generous, because rejecting a real
     inquiry costs a lead and accepting a five-digit one costs nothing. */
  var MAX_QTY_PER_FIELD = 99999;

  var $ = function (s) { return root.querySelector(s); };
  var $$ = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };

  function track(event, payload) {
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.track) {
        window.ShopifyAnalytics.lib.track(event, payload || {}); return;
      }
      if (window.dataLayer && window.dataLayer.push) {
        window.dataLayer.push(Object.assign({ event: event }, payload || {})); return;
      }
    } catch (e) { /* analytics must never break the page */ }
    if (window.console && console.debug) console.debug('[sw-planner]', event, payload || {});
  }

  /* ---------- attribution, carried from the landing page ---------- */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  function attribution() {
    var params = new URLSearchParams(window.location.search);
    var out = {};
    UTM_KEYS.forEach(function (k) {
      var v = params.get(k);
      if (v) { out[k] = v; try { sessionStorage.setItem('sw_' + k, v); } catch (e) {} }
      else { try { var s = sessionStorage.getItem('sw_' + k); if (s) out[k] = s; } catch (e) {} }
    });
    out.landing_url = window.location.href.slice(0, 500);
    out.referring_url = (document.referrer || '').slice(0, 500);
    return out;
  }

  function icon(id, cls) {
    return '<svg class="sw-i ' + (cls || '') + '" aria-hidden="true"><use href="#' + id + '"></use></svg>';
  }

  /* ---------- step 2: what the property has ---------- */
  var tilesEl = $('[data-tiles]');
  var cont2 = $('[data-continue-2]');

  GROUPS.forEach(function (grp) {
    var b = document.createElement('button');
    b.className = 'sw-bp__tile';
    b.type = 'button';
    b.setAttribute('aria-pressed', 'false');
    b.innerHTML = '<span class="sw-bp__tileDot"></span><span>' + grp.label + '</span>';
    b.addEventListener('click', function () {
      picked[grp.g] = !picked[grp.g];
      b.classList.toggle('is-sel', !!picked[grp.g]);
      b.setAttribute('aria-pressed', picked[grp.g] ? 'true' : 'false');
      cont2.disabled = !anyPicked();
    });
    tilesEl.appendChild(b);
  });
  cont2.disabled = true;

  function anyPicked() {
    for (var k in picked) { if (picked[k]) return true; }
    return false;
  }

  /* ---------- step 3: how many ---------- */
  function buildQuantities() {
    var host = $('[data-quantities]');
    host.innerHTML = '';

    GROUPS.filter(function (g) { return picked[g.g]; }).forEach(function (grp) {
      var wrap = document.createElement('div');
      wrap.className = 'sw-bp__qgroup';

      var h = document.createElement('h3');
      h.textContent = grp.label;
      wrap.appendChild(h);

      CATALOG.filter(function (c) { return c.g === grp.g; }).forEach(function (c) {
        if (qty[c.k] === undefined) qty[c.k] = 0;
        if (qtyRaw[c.k] === undefined) qtyRaw[c.k] = qty[c.k] > 0 ? String(qty[c.k]) : '';

        var row = document.createElement('div');
        row.className = 'sw-bp__qrow';
        /* A TEXT field with inputmode="numeric", not <input type="number">.
           A number input accepts '1e3' as valid and hands back 1000, and accepts
           '-5' and '2.5' — so the rules below would be unenforceable in the one
           place the customer can see it. A text field lets us keep exactly what
           was typed and refuse the rest by name.

           AND NO maxlength. It is tempting — five digits is the ceiling — but
           maxlength does not refuse the sixth character, it silently DROPS it.
           Typing 100000 would leave 10000 in the field, with no error, and send
           a tenth of the job to be quoted. An adversarial review caught it; the
           implementer's own jsdom suite could not, because jsdom does not
           enforce maxlength on the value setter. Refusing is readField()'s job,
           and it does it without touching what the customer typed.

           The server checks all of it again and is the authority. */
        var errId = 'bp_qty_err_' + c.k;
        row.innerHTML =
          '<div class="sw-bp__qinfo"><div class="sw-bp__qname"></div><div class="sw-bp__qsize"></div></div>' +
          '<div class="sw-bp__stepper">' +
            '<button type="button" aria-label="Fewer">&minus;</button>' +
            '<input type="text" inputmode="numeric" autocomplete="off" ' +
                   'pattern="[0-9]*" aria-describedby="' + errId + '">' +
            '<button type="button" aria-label="More">+</button>' +
          '</div>' +
          '<p class="sw__fieldErr sw-bp__qerr" id="' + errId + '" role="alert"></p>';

        row.querySelector('.sw-bp__qname').textContent = c.n;
        row.querySelector('.sw-bp__qsize').textContent = c.s;

        var btns = row.querySelectorAll('.sw-bp__stepper button');
        var inp = row.querySelector('.sw-bp__stepper input');
        var err = row.querySelector('.sw-bp__qerr');
        inp.value = qtyRaw[c.k];
        inp.setAttribute('aria-label', c.n + ' ' + c.s + ' quantity');

        /* Reads the field WITHOUT rewriting it. Returns the count, or -1 when
           the field does not hold a usable quantity — the caller decides what
           to say. Nothing here ever turns one number into a different one. */
        function readField() {
          var raw = String(inp.value == null ? '' : inp.value).trim();
          if (raw === '') return 0;
          if (!/^[0-9]+$/.test(raw)) return -1;
          var n = parseInt(raw, 10);
          if (!isFinite(n) || n > MAX_QTY_PER_FIELD) return -1;
          return n;
        }

        function message() {
          var raw = String(inp.value == null ? '' : inp.value).trim();
          if (raw === '' || /^[0-9]+$/.test(raw) === false) {
            return raw === '' ? '' : 'Whole numbers only — digits, no decimals or symbols.';
          }
          if (parseInt(raw, 10) > MAX_QTY_PER_FIELD) {
            return 'That is more than ' + MAX_QTY_PER_FIELD.toLocaleString('en-US')
              + ' on one line. Tell us in the notes and we will price it by hand.';
          }
          return '';
        }

        /* Reveal the alert node BEFORE filling it, the same way setGroupError
           does. .sw__fieldErr is display:none until .is-shown, and text written
           into a display:none role="alert" is written while the node is out of
           the accessibility tree, so screen readers announce nothing. Showing
           first and filling on the next frame makes it a real insertion into a
           live region. This is the field the release exists to protect, so it
           gets the same treatment as the project-type group. */
        function paint(msg) {
          inp.classList.toggle('is-invalid', !!msg);
          if (msg) inp.setAttribute('aria-invalid', 'true');
          else inp.removeAttribute('aria-invalid');
          if (msg) {
            err.classList.add('is-shown');
            if (window.requestAnimationFrame) {
              window.requestAnimationFrame(function () { err.textContent = msg; });
            } else {
              err.textContent = msg;
            }
          } else {
            err.textContent = '';
            err.classList.remove('is-shown');
          }
        }

        /* The +/- buttons operate on the last GOOD value, so pressing + next to
           a field reading "abc" cannot invent a number out of it. */
        function bump(by) {
          var n = readField();
          if (n < 0) return;
          var next = Math.min(MAX_QTY_PER_FIELD, Math.max(0, n + by));
          inp.value = String(next);
          qtyRaw[c.k] = inp.value;
          qty[c.k] = next;
          paint('');
        }

        btns[0].addEventListener('click', function () { bump(-1); });
        btns[1].addEventListener('click', function () { bump(1); });

        inp.addEventListener('input', function () {
          qtyRaw[c.k] = inp.value;
          var n = readField();
          /* An unusable field contributes nothing rather than contributing a
             guess. It is also remembered as unusable, so Continue can refuse. */
          qty[c.k] = n < 0 ? -1 : n;
          if (inp.classList.contains('is-invalid') && !message()) paint('');
        });
        inp.addEventListener('blur', function () { paint(message()); });

        /* Re-entering step 3 rebuilds these rows, so a field that was already
           refused has to come back still refused — otherwise Back then Continue
           walks straight past the problem. */
        if (qty[c.k] === -1) paint(message());

        wrap.appendChild(row);
      });

      host.appendChild(wrap);
    });

  }

  /* ---------- project type (step 1) ----------
     Real radio buttons in a fieldset, not styled divs with click handlers:
     arrow keys move between them, the group has one tab stop, the legend is
     announced, and it submits the same way with JavaScript disabled long
     enough to matter. The slugs are the payload contract with the backend. */
  var PROJECT_TYPES = {
    new_construction: 'New Construction',
    existing_remodel: 'Existing Property / Remodel'
  };

  function projectTypeLabel() {
    return Object.prototype.hasOwnProperty.call(PROJECT_TYPES, projectType)
      ? PROJECT_TYPES[projectType] : '';
  }

  $$('[name="bp_project_type"]').forEach(function (r) {
    r.addEventListener('change', function () {
      if (!r.checked) return;
      projectType = Object.prototype.hasOwnProperty.call(PROJECT_TYPES, r.value) ? r.value : '';
      setGroupError('bp_project_type', '');
    });
  });

  /** Inline error for a radio group, wired to the group's own live region. */
  function setGroupError(name, msg) {
    var box = root.querySelector('[data-err-for="' + name + '"]');
    var group = root.querySelector('[data-group="' + name + '"]');
    if (group) {
      group.classList.toggle('is-invalid', !!msg);
      if (msg) group.setAttribute('aria-invalid', 'true');
      else group.removeAttribute('aria-invalid');
      /* aria-invalid on the fieldset alone is not enough: a fieldset maps to
         role="group", which is not in ARIA's supported-roles list for
         aria-invalid, so most assistive tech ignores it. The radios carry it
         too, and they are what actually gets focus. */
      $$('[name="' + name + '"]').forEach(function (r) {
        if (msg) r.setAttribute('aria-invalid', 'true');
        else r.removeAttribute('aria-invalid');
      });
    }
    if (!box) return;
    if (msg) {
      box.classList.add('is-shown');
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () { box.textContent = msg; });
      } else {
        box.textContent = msg;
      }
    } else {
      box.textContent = '';
      box.classList.remove('is-shown');
    }
  }

  /* ---------- step 4: review and send ----------
     There is no price on this screen and no way to compute one. What the
     customer confirms here is what they told us; the budget itself — both
     materials, side by side — arrives by email. */

  /** Every line the customer actually entered, with its count. */
  function orderedLines() {
    var out = [];
    CATALOG.forEach(function (c) {
      var n = qty[c.k];
      if (typeof n !== 'number' || n <= 0 || !picked[c.g]) return;
      out.push({ c: c, q: n });
    });
    return out;
  }

  function totalSigns() {
    return orderedLines().reduce(function (a, r) { return a + r.q; }, 0);
  }

  /** True when any visible quantity field is holding something unusable. */
  function anyBadQuantity() {
    var bad = false;
    CATALOG.forEach(function (c) {
      if (picked[c.g] && qty[c.k] === -1) bad = true;
    });
    return bad;
  }

  function reviewRow(label, value) {
    var el = document.createElement('div');
    el.className = 'sw-bp__sline';
    var left = document.createElement('span');
    left.textContent = label;
    var right = document.createElement('span');
    right.className = 'q';
    right.textContent = value;
    el.appendChild(left);
    el.appendChild(right);
    return el;
  }

  function renderReview() {
    var host = $('[data-review]');
    if (!host) return;
    host.innerHTML = '';

    var rows = orderedLines();
    var count = totalSigns();

    var about = document.createElement('div');
    about.className = 'sw-bp__reviewBlock';
    var aboutHd = document.createElement('div');
    aboutHd.className = 'sw-bp__summaryHd';
    aboutHd.textContent = 'Your property';
    about.appendChild(aboutHd);
    about.appendChild(reviewRow('Property', $('#bp_prop').value.trim()));
    if ($('#bp_addr').value.trim()) about.appendChild(reviewRow('Address', $('#bp_addr').value.trim()));
    if ($('#bp_mgmt').value.trim()) about.appendChild(reviewRow('Management company', $('#bp_mgmt').value.trim()));
    about.appendChild(reviewRow('Project type', projectTypeLabel()));
    about.appendChild(reviewRow('Contact', $('#bp_name').value.trim()));
    about.appendChild(reviewRow('Email', $('#bp_email').value.trim()));
    if ($('#bp_phone').value.trim()) about.appendChild(reviewRow('Phone', $('#bp_phone').value.trim()));
    about.appendChild(reviewRow('Target need-by date', longDate($('#bp_when').value)));
    host.appendChild(about);

    var signs = document.createElement('div');
    signs.className = 'sw-bp__reviewBlock';
    var signsHd = document.createElement('div');
    signsHd.className = 'sw-bp__summaryHd';
    signsHd.textContent = 'Signs you asked for';
    signs.appendChild(signsHd);

    if (!rows.length) {
      signs.appendChild(reviewRow('Nothing counted yet — go back a step and add a few.', ''));
    } else {
      var byGroup = {};
      rows.forEach(function (r) {
        (byGroup[r.c.g] = byGroup[r.c.g] || []).push(r);
      });
      GROUPS.forEach(function (g) {
        if (!byGroup[g.g]) return;
        var h = document.createElement('h3');
        h.className = 'sw-bp__reviewGroup';
        h.textContent = g.label;
        signs.appendChild(h);
        byGroup[g.g].forEach(function (r) {
          signs.appendChild(reviewRow(r.c.n + ' · ' + r.c.s, String(r.q)));
        });
      });
      var t = document.createElement('div');
      t.className = 'sw-bp__sline sw-bp__sline--total';
      t.innerHTML = '<span>Total signs</span><span></span>';
      t.lastChild.textContent = String(count);
      signs.appendChild(t);
    }
    host.appendChild(signs);

    var notes = $('#bp_notes').value.trim();
    if (notes) {
      var nb = document.createElement('div');
      nb.className = 'sw-bp__reviewBlock';
      var nhd = document.createElement('div');
      nhd.className = 'sw-bp__summaryHd';
      nhd.textContent = 'Quoted separately';
      nb.appendChild(nhd);
      var np = document.createElement('p');
      np.className = 'sw-bp__reviewNotes';
      np.textContent = notes;
      nb.appendChild(np);
      var nn = document.createElement('p');
      nn.className = 'sw-bp__reviewNote';
      nn.textContent = 'These items are not included in either budget. We price them '
        + 'separately and will follow up with that pricing.';
      nb.appendChild(nn);
      host.appendChild(nb);
    }

    var meta = $('[data-meta]');
    if (meta) {
      meta.innerHTML = '';
      var b = document.createElement('b');
      b.textContent = String(count);
      meta.appendChild(b);
      meta.appendChild(document.createTextNode(' sign' + (count === 1 ? '' : 's') + ' on this request'));
    }
  }

  /** "2027-03-15" -> "March 15, 2027", or the raw value if it is not a date. */
  function longDate(v) {
    var raw = String(v == null ? '' : v).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    var d = new Date(raw + 'T00:00:00Z');
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  }

  /* ---------- navigation ---------- */
  function go(n) {
    /* A send that failed leaves a red banner on step 4. Walking back and
       forward must not bring it along — the customer has not tried again yet. */
    var eb = $('[data-error]');
    if (eb) { eb.textContent = ''; eb.classList.remove('is-shown'); }
    if (n === 2 && !validateStep1()) return;
    if (n === 4 && !validateNeedBy()) return;
    if (n === 4 && !validateQuantities()) return;
    if (n === 3) buildQuantities();
    if (n === 4) {
      renderReview();
      /* Same event name as before, on purpose — the history is worth more than
         a tidier name. `total` is gone because the browser no longer knows one;
         `signs` is what this screen is actually about now. */
      track('budget_result_viewed', { signs: totalSigns(), step: 'review_and_send' });
    }

    $$('.sw-bp__panel').forEach(function (p) { p.classList.remove('is-active'); });
    $('[data-panel="' + n + '"]').classList.add('is-active');

    $$('.sw-bp__railStep').forEach(function (s) {
      var i = parseInt(s.getAttribute('data-rail'), 10);
      s.classList.toggle('is-active', i === n);
      s.classList.toggle('is-done', i < n);
    });

    var rail = root.querySelector('.sw-bp__rail');
    if (rail) rail.scrollIntoView({ behavior: 'smooth', block: 'start' });
    track('planner_step_completed', { step: n });
  }

  $$('[data-go]').forEach(function (b) {
    b.addEventListener('click', function () { go(parseInt(b.getAttribute('data-go'), 10)); });
  });

  /* ---------- field validation ----------
     Email and phone are checked here AND again on the server. The server
     is the authority; this only saves the customer a round trip.

     EMAIL_RE is the site's single email rule. The same pattern lives in
     sw-landing.js and in normalizeEmail() in the backend's lead-guard.js.
     It insists on a real 2+ letter TLD because the budget estimate IS the
     email — "lakeview.c" is a lost lead, not a cosmetic typo. Change all
     three together or not at all. */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

  /* Returns '(###) ###-####' for a valid U.S. number, or null.
     Blank is the caller's problem — phone is optional.

     NANP_RE, not a length check: neither the area code nor the exchange may
     begin with 0 or 1. Counting to ten accepts "0000000000" and, worse,
     turns "1234567890 x1" into eleven digits beginning with 1, eats the 1 as
     a country code, and hands back (234) 567-8901 — a different number, with
     nobody told. The same pattern lives in the backend's lead-guard.js. */
  var NANP_RE = /^[2-9]\d{2}[2-9]\d{6}$/;

  function normalizeUsPhone(raw) {
    var s = String(raw == null ? '' : raw);
    /* Letters mean an extension or a note — "x203", "ext. 12". Stripping them
       silently glues the extension's digits onto the number, and when the
       total lands on eleven starting with 1 the leading digit is then eaten
       as a country code and a DIFFERENT, valid-looking number comes out. */
    if (/[A-Za-z]/.test(s)) return null;
    var d = s.replace(/[^0-9]/g, '');
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    if (!NANP_RE.test(d)) return null;
    return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
  }

  function setFieldError(el, msg) {
    var box = root.querySelector('[data-err-for="' + el.id + '"]');
    el.classList.toggle('is-invalid', !!msg);
    if (msg) el.setAttribute('aria-invalid', 'true');
    else el.removeAttribute('aria-invalid');
    if (!box) return;
    if (msg) {
      /* A display:none subtree is not in the accessibility tree at all, so
         text written into this region while it is hidden announces nothing:
         the customer hears "invalid entry" from aria-invalid and never hears
         why. Revealing and writing in the same task does not help — both are
         synchronous, no style recalc happens between them, and the resulting
         a11y tree diff is identical either way. Writing on the next frame
         forces the recalc in between, so the text lands as an insertion into
         a live region that is already present. */
      box.classList.add('is-shown');
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () { box.textContent = msg; });
      } else {
        box.textContent = msg;
      }
    } else {
      box.textContent = '';
      box.classList.remove('is-shown');
    }
  }

  /* Pure: takes a value, returns the message. '' means valid. Nothing in
     here touches the field, so it is safe to call on every keystroke. */
  function emailError(v) {
    v = String(v == null ? '' : v).trim();
    if (!v) return 'Please enter your email address.';
    if (!EMAIL_RE.test(v)) return 'That email address does not look right — try you@company.com.';
    return '';
  }

  function phoneError(v) {
    v = String(v == null ? '' : v).trim();
    if (!v) return '';   /* phone is optional */
    if (!normalizeUsPhone(v)) return 'Please enter a valid U.S. phone number or leave this field blank.';
    return '';
  }

  /* Fields the customer has actually typed in. Tabbing past an empty
     Email should not paint it red — Continue is what enforces required. */
  var touched = {};

  /* Settles the field: trims, reformats a good phone number, shows or
     clears the inline message. Only ever called on blur and on Continue,
     never mid-keystroke. `quiet` suppresses the empty-field complaint,
     which is what blur passes. Returns the message shown. */
  function settleEmail(quiet) {
    var el = $('#bp_email');
    el.value = el.value.trim();
    /* Blur on a field the customer has never typed in leaves whatever state
       Continue last set. Returning early rather than settling on an empty
       message matters: falling through would CLEAR a required error that
       Continue legitimately raised, so tabbing past the field would wipe the
       red and leave a form that looks clean but still will not advance. */
    if (quiet && !el.value && !touched.bp_email) return '';
    var msg = emailError(el.value);
    setFieldError(el, msg);
    return msg;
  }

  function settlePhone() {
    var el = $('#bp_phone');
    el.value = el.value.trim();
    var msg = phoneError(el.value);
    if (!msg && el.value) el.value = normalizeUsPhone(el.value);
    setFieldError(el, msg);
    return msg;
  }

  [['#bp_email', settleEmail, emailError],
   ['#bp_phone', settlePhone, phoneError]].forEach(function (f) {
    var el = $(f[0]);
    if (!el) return;
    el.addEventListener('blur', function () { f[1](true); });
    /* clears an error the moment it is fixed, but never raises one and
       never rewrites what is being typed */
    el.addEventListener('input', function () {
      touched[el.id] = true;
      if (el.classList.contains('is-invalid') && !f[2](el.value)) setFieldError(el, '');
    });
  });

  function validateStep1() {
    var ok = true, firstBad = null;

    ['#bp_prop', '#bp_name'].forEach(function (sel) {
      var el = $(sel);
      el.value = el.value.trim();
      var bad = !el.value;
      el.classList.toggle('is-invalid', bad);
      /* These two carry no inline message yet, so without aria-invalid the
         only signal that they failed is a red border — failure conveyed by
         colour alone, which is WCAG 2.1 3.3.1 and 1.4.1 at Level A. The
         message itself needs approved copy and is a separate change. */
      if (bad) el.setAttribute('aria-invalid', 'true');
      else el.removeAttribute('aria-invalid');
      if (bad) { ok = false; if (!firstBad) firstBad = el; }
    });

    if (settleEmail(false)) { ok = false; if (!firstBad) firstBad = $('#bp_email'); }
    if (settlePhone()) { ok = false; if (!firstBad) firstBad = $('#bp_phone'); }

    if (!projectTypeLabel()) {
      setGroupError('bp_project_type', 'Please tell us whether this is new construction or an existing property.');
      ok = false;
      if (!firstBad) firstBad = root.querySelector('[name="bp_project_type"]');
    } else {
      setGroupError('bp_project_type', '');
    }

    if (firstBad) { firstBad.focus(); track('form_error', { form: 'planner_step1' }); }
    return ok;
  }

  /* Refuses to advance while any visible quantity field holds something we
     would have to guess about. The field itself already says what is wrong;
     this puts the focus on the first one and stops the customer reaching a
     review screen that quietly omitted a line they typed. */
  function validateQuantities() {
    var bad = null;
    CATALOG.forEach(function (c) {
      if (bad || !picked[c.g] || qty[c.k] !== -1) return;
      bad = root.querySelector('#bp_qty_err_' + c.k);
    });
    if (!bad) return true;
    var field = bad.parentNode.querySelector('input');
    if (field) field.focus();
    track('form_error', { form: 'planner_quantities' });
    return false;
  }

  function validateNeedBy() {
    var el = $('#bp_when');
    if (!el) return true;
    var v = (el.value || '').trim();
    var ok = /^\d{4}-\d{2}-\d{2}$/.test(v);
    if (ok) {
      var d = new Date(v + 'T00:00:00Z');
      ok = !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
    }
    el.classList.toggle('is-invalid', !ok);
    if (!ok) { el.focus(); track('form_error', { form: 'planner_need_by' }); }
    return ok;
  }

  /* ---------- submit ---------- */
  var submitBtn = $('[data-submit]');
  var errBox = $('[data-error]');

  submitBtn.addEventListener('click', function () {
    if (submitting) return;

    if (!validateQuantities()) return;

    var quantities = {};
    CATALOG.forEach(function (c) {
      var n = qty[c.k];
      if (typeof n === 'number' && n > 0 && picked[c.g]) quantities[c.k] = n;
    });

    var hp = $('#bp_website');
    var payload = Object.assign({
      property_name:    $('#bp_prop').value.trim(),
      property_address: $('#bp_addr').value.trim(),
      management:       $('#bp_mgmt').value.trim(),
      contact_name:     $('#bp_name').value.trim(),
      email:            $('#bp_email').value.trim(),
      phone:            $('#bp_phone').value.trim(),
      project_type:     projectType,
      need_by_date:     $('#bp_when').value,
      notes:            $('#bp_notes').value.trim(),
      /* `finish` and `budget_shown` are gone. The customer no longer picks a
         material — every budget quotes both — and this page has no price to
         echo. The backend still accepts both from a cached page. */
      quantities:       quantities,
      source:           'Website',
      company_website:  hp ? hp.value : ''
    }, attribution());

    errBox.classList.remove('is-shown');
    submitting = true;
    submitBtn.disabled = true;
    var original = submitBtn.textContent;
    submitBtn.textContent = 'Sending…';
    /* Historic event name, kept deliberately. `total` is gone — the browser
       does not know one, and putting price back into the API just to feed
       analytics would undo the whole point of PR 3. */
    track('formal_estimate_requested', {
      signs: payload.quantities,
      total_signs: totalSigns(),
      project_type: projectType
    });

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; })
          .then(function (body) { return { ok: res.ok, body: body }; });
      })
      .then(function (r) {
        if (!r.ok || !r.body.ok) {
          var said = r.body && (r.body.error || (r.body.errors || []).join(' '));
          var e = new Error(said || 'Request failed');
          /* Only a message the server actually wrote is worth showing. */
          e.serverSaid = !!said;
          throw e;
        }
        /* The server decides which ending this is. A project over 1,000 signs
           is accepted in full but not automatically priced, so it gets the
           Under Review completion rather than the "check your email" one.
           Anything unexpected falls back to the normal ending, because a
           customer whose request WAS filed must never be told it was not. */
        /* Trimmed and lower-cased rather than compared raw: the fallback
           direction is the safe one either way, but a customer whose project
           IS under review should not be told to check their inbox because the
           server said 'Manual'. */
        var manual = String((r.body && r.body.review) || '').trim().toLowerCase() === 'manual';
        var doneEmail = $('[data-done-email]');
        if (doneEmail) doneEmail.textContent = payload.email;
        $$('.sw-bp__panel').forEach(function (p) { p.classList.remove('is-active'); });
        $('[data-panel="' + (manual ? 6 : 5) + '"]').classList.add('is-active');
        track('budget_request_completed', {
          review: manual ? 'manual' : 'automated',
          total_signs: (r.body && r.body.total_signs) || totalSigns()
        });
        $$('.sw-bp__railStep').forEach(function (s) { s.classList.add('is-done'); s.classList.remove('is-active'); });
        var rail = root.querySelector('.sw-bp__rail');
        if (rail) rail.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
      .catch(function (err) {
        if (window.console) console.error('[sw-planner]', err);
        /* When the server told us WHY, say that. "Please try again" is the right
           answer to a network blip and exactly the wrong one to "no quantities
           were submitted" — retrying can never fix that, and the customer is
           left pressing a button that cannot work. `serverSaid` is only ever set
           from a 4xx body, so a 5xx still gets the generic apology. */
        errBox.textContent = err && err.serverSaid
          ? err.message
          : "We couldn't send that just now. Please try again, or email hello@signageworks.com and we'll take it down for you.";
        errBox.classList.add('is-shown');
        submitting = false;
        submitBtn.disabled = false;
        submitBtn.textContent = original;
        track('form_error', { form: 'planner_submit' });
      });
  });

  track('planner_started', {});
})();
